import React, { useRef, useState, useEffect, useCallback } from 'react';
import PressableScale from './motion/PressableScale';
import { View, Text, StyleSheet, Platform, Animated, Easing, TouchableOpacity, TextInput, Dimensions, Modal, ScrollView , Pressable } from 'react-native';
import { useNavigation } from '../context/NavigationContext';
import { useAuth } from '../context/AuthContext';
import Svg, { Path, Defs, LinearGradient as SvgLinearGradient, Stop, Mask } from 'react-native-svg';
import { Image } from 'react-native';
import CommunityModal from './CommunityModal';
import GameTicker from './GameTicker';
import InjuryTicker from './InjuryTicker';
import { useTicker } from '../context/TickerContext';
import { db } from '../firebaseConfig';
import { collection, doc, query, where, orderBy, limit as firestoreLimit, getDocs, updateDoc, onSnapshot } from 'firebase/firestore';
import { Ionicons } from '@expo/vector-icons';

import { isMobile, isTablet, SCREEN_WIDTH as screenWidth } from '../utils/responsive';
import { useUiStyle } from '../context/UiStyleContext';
import { FONT, FL, ACCENT_GRADIENT } from './floodlight/theme';

// Scroll threshold for direction detection
const SCROLL_THRESHOLD = 10;

// Ticker height constant
const TICKER_HEIGHT = 40;

// Helper function for responsive padding
const getNavBarPadding = () => {
  if (Platform.OS !== 'web') return 16;
  if (isMobile) return 16;
  if (isTablet) return 32; // Smaller padding for tablets
  return 64; // Desktop padding
};

const styles = StyleSheet.create({
  // Outer wrapper for fixed positioning
  outerWrapper: {
    width: '100%',
    zIndex: 9999,
    ...(Platform.OS === 'web' ? {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
    } : {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
    }),
  },
  // Animated wrapper for navbar (separate from ticker)
  navAnimatedWrapper: {
    width: '100%',
    zIndex: 9998,
    marginTop: 10, // Space between ticker and navbar
  },
  // Collapsed ticker bar
  collapsedTicker: {
    height: 24,
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  collapsedTickerText: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.4)',
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
    letterSpacing: 0.5,
  },
  // Navigation Bar Styles
  navContainer: { 
    width: '100%',
    zIndex: 9999,
    position: 'relative',
    paddingHorizontal: isMobile ? 16 : isTablet ? 32 : 64,
  },
  navBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: isTablet ? 6 : 8, // Slightly smaller vertical padding for tablets
    paddingHorizontal: getNavBarPadding(),
    borderRadius: 100,
    borderWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.25)',
    borderLeftColor: 'rgba(255,255,255,0.18)',
    borderRightColor: 'rgba(0,0,0,0.35)',
    borderBottomColor: 'rgba(0,0,0,0.45)',
    backgroundColor: '#000'
  },
  navLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  navCenter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Platform.OS === 'web' ? (isTablet ? 20 : 28) : 18, // Reduced gap for tablets
  },
  navItemWrap: { alignItems: 'center' },
  navRight: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 18,
  },
  navItem: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" : 'System',
    fontSize: Platform.OS === 'web' ? (isTablet ? 14 : 15) : 13, // Smaller font for tablets
    fontWeight: '800',
    lineHeight: Platform.OS === 'web' ? (isTablet ? 17 : 18) : 16, // Adjusted line height for tablets
    textAlign: 'center',
    color: 'rgba(255,255,255,0.9)'
  },
  hamburgerButton: {
    padding: 8,
  },
  mobileMenuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  mobileMenuContainer: {
    width: '80%',
    maxWidth: 320,
    height: '100%',
    backgroundColor: '#1a1a1a',
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255,255,255,0.1)',
  },
  mobileMenuHeader: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mobileMenuHeaderText: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
    fontSize: 18,
    fontWeight: '700',
    color: 'rgba(255,255,255,0.9)',
  },
  mobileMenuProfile: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  mobileMenuProfileImage: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  mobileMenuProfileInfo: {
    flex: 1,
  },
  mobileMenuProfileName: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
    fontSize: 16,
    fontWeight: '700',
    color: 'rgba(255,255,255,0.95)',
    marginBottom: 4,
  },
  mobileMenuProfileEmail: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
    fontSize: 13,
    fontWeight: '400',
    color: 'rgba(255,255,255,0.6)',
  },
  mobileMenuItems: {
    paddingVertical: 12,
  },
  mobileMenuItem: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.05)',
  },
  mobileMenuItemText: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
    fontSize: 16,
    fontWeight: '600',
    color: 'rgba(255,255,255,0.85)',
  },
  mobileMenuItemActive: {
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  toastWrap: {
    position: 'fixed',
    right: 24,
    bottom: 24,
    maxWidth: 360,
    zIndex: 9999,
  },
  toastCard: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    backgroundColor: 'rgba(0,0,0,0.75)'
  },
  toastTitle: { color: '#fff', fontWeight: '700' },
  toastText: { color: 'rgba(255,255,255,0.85)', marginTop: 2 },
  toastActions: { flexDirection: 'row', gap: 8, marginTop: 8 },
  toastBtn: { paddingVertical: 6, paddingHorizontal: 10, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.15)' },
  toastBtnText: { color: '#fff', fontWeight: '600' },
  navItemDimmed: {
    color: 'rgba(200,200,200,0.6)'
  },
  navUnderline: {
    marginTop: 3,
    height: 2,
    width: '100%',
    backgroundColor: 'rgba(255,255,255,0.5)',
    transformOrigin: 'left',
  },
  iconWrap: {
    position: 'relative',
    padding: 2,
    borderRadius: 20,
  },


});

/*
 * Floodlight navbar (v1.71): the same floating pill, restyled to the
 * Floodlight glass language: translucent black glass with backdrop blur, one
 * hairline border, Archivo caps links with tracking, a purple glass pill for
 * the active link, round glass icon buttons, and a glass sheet for the mobile
 * menu. Used only when useUiStyle().isFloodlight; Classic renders `styles`.
 */
const WEB = Platform.OS === 'web';
const FL_BLUR = WEB ? { backdropFilter: 'blur(20px) saturate(150%)', WebkitBackdropFilter: 'blur(20px) saturate(150%)' } : {};
const FL_ACCENT_TEXT = '#C4A1FF';
const FL_ACCENT_WASH = 'rgba(124,58,237,0.20)';
const FL_ACCENT_LINE = 'rgba(167,139,250,0.42)';
const FL_SHEET = 'rgba(12,12,18,0.82)';
const flStyles = StyleSheet.create({
  navBar: {
    paddingVertical: 6,
    paddingHorizontal: isMobile ? 8 : isTablet ? 12 : 14,
    borderRadius: 999,
    borderWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.16)',
    borderLeftColor: FL.hair,
    borderRightColor: FL.hair,
    borderBottomColor: FL.hair,
    backgroundColor: 'rgba(10,10,14,0.62)',
    ...FL_BLUR,
    ...(WEB ? { boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 12px 32px rgba(0,0,0,0.38)' } : {}),
  },
  navLeft: { paddingLeft: 4 },
  navCenter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: isTablet ? 2 : 4,
  },
  navRight: { gap: 8 },
  link: {
    minHeight: 40,
    paddingHorizontal: isTablet ? 12 : 16,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  linkHover: { backgroundColor: 'rgba(255,255,255,0.06)', borderColor: FL.hair },
  linkPressed: { backgroundColor: 'rgba(255,255,255,0.10)' },
  linkActive: { backgroundColor: FL_ACCENT_WASH, borderColor: FL_ACCENT_LINE },
  linkText: {
    fontFamily: FONT,
    fontSize: isTablet ? 12 : 12.5,
    lineHeight: 16,
    fontWeight: '600',
    letterSpacing: isTablet ? 1.4 : 1.75,
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.62)',
    ...(WEB ? { fontStretch: '108%' } : {}),
  },
  linkTextHover: { color: 'rgba(255,255,255,0.92)' },
  linkTextActive: { color: '#FFFFFF' },
  cta: { borderWidth: 0, ...(WEB ? { backgroundImage: ACCENT_GRADIENT } : { backgroundColor: '#7C3AED' }) },
  ctaHover: WEB ? { boxShadow: '0 6px 20px rgba(124,58,237,0.45)' } : {},
  ctaText: { color: '#FFFFFF' },
  iconButton: {
    width: isMobile ? 44 : 40,
    height: isMobile ? 44 : 40,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: FL.hair,
    backgroundColor: 'rgba(255,255,255,0.05)',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  iconButtonHover: { backgroundColor: 'rgba(255,255,255,0.10)', borderColor: 'rgba(255,255,255,0.20)' },
  iconButtonOn: { backgroundColor: FL_ACCENT_WASH, borderColor: FL_ACCENT_LINE },
  badge: {
    position: 'absolute', top: 2, right: 2, minWidth: 18, height: 18, paddingHorizontal: 4,
    borderRadius: 999, backgroundColor: '#FF4444', alignItems: 'center', justifyContent: 'center',
    borderWidth: 2, borderColor: '#0A0A0E',
  },
  badgeText: { color: '#FFF', fontSize: 10, fontWeight: '700', fontFamily: FONT, ...(WEB ? { fontVariant: ['tabular-nums'] } : {}) },
  avatar: {
    borderRadius: 999,
    padding: 2,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  avatarHover: { borderColor: FL_ACCENT_LINE },
  avatarActive: { borderColor: FL_ACCENT_LINE, backgroundColor: FL_ACCENT_WASH },
  // Mobile menu: a glass sheet from the right.
  mobileMenuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    ...(WEB ? { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' } : {}),
  },
  mobileMenuContainer: {
    width: '84%',
    maxWidth: 340,
    height: '100%',
    backgroundColor: FL_SHEET,
    borderLeftWidth: 1,
    borderLeftColor: FL.hair,
    borderTopLeftRadius: 24,
    borderBottomLeftRadius: 24,
    overflow: 'hidden',
    ...FL_BLUR,
    ...(WEB ? { boxShadow: '-24px 0 60px rgba(0,0,0,0.5)' } : {}),
  },
  mobileMenuHeader: {
    paddingVertical: 14,
    paddingLeft: 24,
    paddingRight: 14,
    borderBottomWidth: 1,
    borderBottomColor: FL.hair,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mobileMenuHeaderText: {
    fontFamily: FONT,
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.56)',
  },
  mobileMenuProfile: {
    marginHorizontal: 12,
    marginTop: 12,
    padding: 14,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: FL.hair,
    backgroundColor: 'rgba(255,255,255,0.04)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  mobileMenuProfileName: {
    fontFamily: FONT,
    fontSize: 16,
    fontWeight: '700',
    color: 'rgba(255,255,255,0.95)',
    marginBottom: 2,
  },
  mobileMenuProfileEmail: {
    fontFamily: FONT,
    fontSize: 13,
    color: 'rgba(255,255,255,0.56)',
  },
  mobileMenuItems: { paddingVertical: 12, paddingHorizontal: 12 },
  mobileMenuItem: {
    minHeight: 52,
    paddingHorizontal: 16,
    marginBottom: 4,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'transparent',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  mobileMenuItemHover: { backgroundColor: 'rgba(255,255,255,0.05)' },
  mobileMenuItemActive: { backgroundColor: FL_ACCENT_WASH, borderColor: FL_ACCENT_LINE },
  mobileMenuItemText: {
    fontFamily: FONT,
    fontSize: 15,
    fontWeight: '600',
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.78)',
    ...(WEB ? { fontStretch: '108%' } : {}),
  },
  mobileMenuItemTextActive: { color: '#FFFFFF' },
  mobileMenuDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: FL_ACCENT_TEXT },
  // Notifications dropdown: the same glass.
  notifPanel: {
    backgroundColor: FL_SHEET,
    borderRadius: 20,
    borderColor: FL.hair,
    overflow: 'hidden',
    ...FL_BLUR,
    ...(WEB ? { boxShadow: '0 18px 48px rgba(0,0,0,0.5)' } : {}),
  },
  notifTitle: { fontFamily: FONT, fontSize: 12, fontWeight: '600', letterSpacing: 1.6, textTransform: 'uppercase', color: 'rgba(255,255,255,0.72)' },
});

/** Floodlight nav link: Archivo caps, glass pill on hover, purple glass pill when active. */
const FlNavItem = ({ label, onPress, active, cta }) => {
    const [hovered, setHovered] = useState(false);
    return (
      <PressableScale
        onPress={onPress}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        accessibilityRole="button"
        accessibilityState={active ? { selected: true } : undefined}
        {...(WEB && active ? { 'aria-current': 'page' } : {})}
        style={[flStyles.link, cta && flStyles.cta, active && flStyles.linkActive]}
        hoverStyle={cta ? flStyles.ctaHover : active ? null : flStyles.linkHover}
        pressedStyle={cta ? null : flStyles.linkPressed}
        dataSet={{ pressable: 'scale', flNav: cta ? 'cta' : active ? 'active' : 'link' }}
      >
        <Text style={[flStyles.linkText, (hovered || active) && flStyles.linkTextHover, active && flStyles.linkTextActive, cta && flStyles.ctaText]}>
          {label}
        </Text>
      </PressableScale>
    );
};

// Navigation Bar Component
const NavItem = ({ label, onPress, dimmed, fl, active, cta }) => {
    if (fl) return <FlNavItem label={label} onPress={onPress} active={active} cta={cta} />;
    return <ClassicNavItem label={label} onPress={onPress} dimmed={dimmed} />;
};

const ClassicNavItem = ({ label, onPress, dimmed }) => {
    const hoverT = useRef(new Animated.Value(0)).current;
    const onIn = () => Animated.timing(hoverT, { toValue: 1, duration: 320, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: false }).start();
    const onOut = () => Animated.timing(hoverT, { toValue: 0, duration: 320, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: false }).start();
    const underlineStyle = {
      transform: [{ scaleX: hoverT }],
      opacity: hoverT,
    };
    return (
      <PressableScale onPress={onPress} {...(Platform.OS === 'web' ? { onMouseEnter: onIn, onMouseLeave: onOut } : {})}>
        <View style={styles.navItemWrap}>
          <Text style={[styles.navItem, dimmed ? styles.navItemDimmed : null, Platform.OS === 'web' ? ({} /* default solid; gradient on hover */) : null, ]}>
            {label}
          </Text>
          {Platform.OS === 'web' ? (
            <Animated.View style={[styles.navUnderline, underlineStyle]} />
          ) : null}
        </View>
      </PressableScale>
    );
  };
  
  
  
// Logo component using app icon
const LogoIcon = ({ size = 32 }) => (
    <Image source={require('../assets/icon.png')} style={{ width: size, height: size, borderRadius: 8 }} resizeMode="contain" />
  );
  
  // Profile Icon Component
  const ProfileIcon = ({ size = 40 }) => (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size} viewBox="0 0 41 41">
        <Defs>
          <SvgLinearGradient id="profileGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <Stop offset="0%" stopColor="#FFF" stopOpacity="1" />
            <Stop offset="100%" stopColor="#FFF" stopOpacity="0.8" />
          </SvgLinearGradient>
          <Mask id="circleMask">
            <Path
              d="M0.976562 20.9314C0.976562 9.8857 9.93087 0.931396 20.9766 0.931396C32.0223 0.931396 40.9766 9.8857 40.9766 20.9314C40.9766 31.9771 32.0223 40.9314 20.9766 40.9314C9.93087 40.9314 0.976562 31.9771 0.976562 20.9314Z"
              fill="white"
            />
          </Mask>
        </Defs>
        <Path
          d="M20.9766 40.9314V38.9314C11.0354 38.9314 2.97656 30.8725 2.97656 20.9314H0.976562H-1.02344C-1.02344 33.0817 8.8263 42.9314 20.9766 42.9314V40.9314ZM40.9766 20.9314H38.9766C38.9766 30.8725 30.9177 38.9314 20.9766 38.9314V40.9314V42.9314C33.1268 42.9314 42.9766 33.0817 42.9766 20.9314H40.9766ZM20.9766 0.931396V2.9314C30.9177 2.9314 38.9766 10.9903 38.9766 20.9314H40.9766H42.9766C42.9766 8.78113 33.1268 -1.0686 20.9766 -1.0686V0.931396ZM20.9766 0.931396V-1.0686C8.8263 -1.0686 -1.02344 8.78113 -1.02344 20.9314H0.976562H2.97656C2.97656 10.9903 11.0354 2.9314 20.9766 2.9314V0.931396Z"
          fill="url(#profileGradient)"
          mask="url(#circleMask)"
          strokeWidth="2"
        />
        <Path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M30.6885 35.9276C30.8252 35.9276 30.9565 35.8744 31.0546 35.7791C31.1526 35.6839 31.2096 35.5542 31.2135 35.4176C31.3107 33.9593 31.1995 32.4945 30.8835 31.0676C30.7492 30.4499 30.5687 29.8432 30.3435 29.2526C30.1194 28.6619 29.8486 28.0901 29.5335 27.5426C28.8209 26.3314 27.8346 25.304 26.6535 24.5426C27.3945 23.8868 27.9837 23.0774 28.3801 22.1707C28.7764 21.264 28.9704 20.2818 28.9485 19.2926C29.053 18.0023 28.9363 16.7036 28.6035 15.4526C28.3197 14.6078 27.8711 13.8277 27.2835 13.1576C27.236 13.1081 27.1789 13.0688 27.1157 13.0419C27.0526 13.015 26.9847 13.0012 26.916 13.0012C26.8474 13.0012 26.7795 13.015 26.7164 13.0419C26.6532 13.0688 26.5961 13.1081 26.5485 13.1576C26.4417 13.2455 26.3739 13.372 26.3599 13.5096C26.3458 13.6473 26.3867 13.7849 26.4735 13.8926C27.0731 14.6109 27.4716 15.4752 27.6285 16.3976C27.7495 17.3688 27.7495 18.3513 27.6285 19.3226C27.6149 20.5157 27.2097 21.6713 26.4753 22.6117C25.7408 23.5521 24.7178 24.2252 23.5635 24.5276C22.3951 24.8424 21.1707 24.8899 19.9813 24.6666C18.792 24.4433 17.6682 23.9548 16.6935 23.2376C16.0253 22.6928 15.543 21.9537 15.3135 21.1226C15.023 20.2541 14.9257 19.3326 15.0285 18.4226C15.1596 17.1076 15.6018 15.8428 16.3185 14.7326C17.0225 13.6528 18.0849 12.856 19.3185 12.4826C20.263 12.1966 21.242 12.0402 22.2285 12.0176C23.1995 11.9975 24.1642 12.1762 25.0635 12.5426C25.1729 12.5923 25.2976 12.5965 25.4101 12.5543C25.5227 12.5121 25.6138 12.427 25.6635 12.3176C25.7133 12.2082 25.7175 12.0835 25.6753 11.971C25.6331 11.8584 25.5479 11.7673 25.4385 11.7176C24.4401 11.2382 23.3508 10.9774 22.2435 10.9526C21.1533 10.929 20.0644 11.045 19.0035 11.2976C17.4747 11.694 16.1292 12.6052 15.1935 13.8776C14.272 15.1471 13.6653 16.6175 13.4235 18.1676C13.2479 19.333 13.3403 20.5232 13.6935 21.6476C14.0256 22.8367 14.7301 23.8881 15.7035 24.6476L16.0335 24.8576C14.6512 25.4974 13.4462 26.4655 12.5235 27.6776C11.9036 28.4909 11.4267 29.404 11.1135 30.3776C10.8126 31.3503 10.6461 32.3597 10.6185 33.3776C10.5331 34.1144 10.493 34.8558 10.4985 35.5976C10.5003 35.9915 10.5873 36.3804 10.7535 36.7376C10.8138 36.8444 10.9126 36.9241 11.0298 36.9604C11.1469 36.9967 11.2735 36.9867 11.3835 36.9326C11.4378 36.9041 11.486 36.8653 11.5252 36.8182C11.5644 36.7711 11.5939 36.7167 11.6121 36.6581C11.6302 36.5996 11.6365 36.538 11.6308 36.477C11.625 36.416 11.6073 36.3567 11.5785 36.3026C11.4952 35.988 11.4952 35.6571 11.5785 35.3426C11.6835 34.6676 11.9085 33.9476 11.9985 33.5426C12.1576 32.6733 12.4091 31.8235 12.7485 31.0076C13.0537 30.2022 13.446 29.4326 13.9185 28.7126C14.7761 27.4122 15.9336 26.3371 17.2935 25.5776C19.4297 26.5465 21.8463 26.7013 24.0885 26.0126C24.7286 25.8023 25.3387 25.5099 25.9035 25.1426C26.9741 26.1943 27.7899 27.477 28.2885 28.8926C28.6485 29.7176 28.9785 30.5726 29.2635 31.4276C29.732 32.7208 30.0442 34.0653 30.1935 35.4326C30.2007 35.5615 30.2552 35.6833 30.3465 35.7746C30.4378 35.8659 30.5596 35.9204 30.6885 35.9276Z"
          fill="white"
        />
      </Svg>
    </View>
  );
  

function NavigationHeader({ showTicker = true }) {
    // ✅ FIX: Get all properties from useNavigation in one call
    const { 
        navigateTo, 
        currentScreen, 
        isAdmin, 
        userType,
        communityModalEnabled,  // ✅ Added
        communityRedirectUrl,   // ✅ Added
        communityScreenEnabled  // ✅ Feature flag: true = full community screen, false = waitlist modal
    } = useNavigation();
    const { user, userProfile, logout } = useAuth();
    // Floodlight restyles the bar (glass pill, Archivo links); Classic renders exactly as before.
    const { isFloodlight: fl } = useUiStyle();
    // Redundant admin check: NavigationContext listener + AuthContext userProfile both read the same
    // Firestore doc — if either correctly reports admin, show the link.
    const effectiveIsAdmin = isAdmin || userProfile?.userType?.toLowerCase() === 'admin';
    const navOpacity = useRef(new Animated.Value(1)).current;
    
    // Ticker context
    const { 
      isTickerEnabled, 
      isTickerCollapsed,
      isTickerVisible,
      fixtures, 
      toggleCollapsed,
      isLoading: tickerLoading,
      isLoadingMore: tickerLoadingMore,
      isInjuryTickerEnabled
    } = useTicker();
    
    // Scroll-aware navigation state
    const headerTranslateY = useRef(new Animated.Value(0)).current;
    const lastScrollY = useRef(0);
    const isNavHidden = useRef(false);
    const [headerHeight, setHeaderHeight] = useState(100);
    
    // Check if user is logged in from AuthContext
    const isLoggedIn = user !== null;
    
    // Scroll-aware navigation functions
    const showNavBar = useCallback(() => {
        if (!isNavHidden.current) return;
        isNavHidden.current = false;
        
        Animated.spring(headerTranslateY, {
            toValue: 0,
            damping: 20,
            stiffness: 300,
            mass: 0.8,
            useNativeDriver: true,
        }).start();
    }, []);
    
    const hideNavBar = useCallback(() => {
        if (isNavHidden.current) return;
        isNavHidden.current = true;
        
        // Account for ticker + injury ticker height when calculating hide distance
        // When tickers are visible, navbar starts lower so needs more translation
        const tickerVisible = isTickerVisible && showTicker;
        const injuryTickerVisible = tickerVisible && isInjuryTickerEnabled;
        const INJURY_TICKER_H = 36;
        const hideDistance = headerHeight + (tickerVisible ? TICKER_HEIGHT : 0) + (injuryTickerVisible ? INJURY_TICKER_H : 0) + 20;
        
        Animated.spring(headerTranslateY, {
            toValue: -hideDistance,
            damping: 20,
            stiffness: 300,
            mass: 0.8,
            useNativeDriver: true,
        }).start();
    }, [headerHeight, isTickerVisible, showTicker, isInjuryTickerEnabled]);
    
    const resetNavBar = useCallback(() => {
        isNavHidden.current = false;
        Animated.spring(headerTranslateY, {
            toValue: 0,
            damping: 25,
            stiffness: 400,
            mass: 0.6,
            useNativeDriver: true,
        }).start();
    }, []);
    
    // Web scroll listener for scroll-aware navbar
    useEffect(() => {
        if (Platform.OS !== 'web') return;
        
        let ticking = false;
        
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const currentScrollY = window.scrollY || document.documentElement.scrollTop;
                    const diff = currentScrollY - lastScrollY.current;
                    
                    // At top of page - always show navbar in default position
                    if (currentScrollY <= 10) {
                        resetNavBar();
                        lastScrollY.current = currentScrollY;
                        ticking = false;
                        return;
                    }
                    
                    // Scrolling down - hide navbar
                    if (diff > SCROLL_THRESHOLD && currentScrollY > 50) {
                        hideNavBar();
                    }
                    // Scrolling up - show navbar
                    else if (diff < -SCROLL_THRESHOLD) {
                        showNavBar();
                    }
                    
                    lastScrollY.current = currentScrollY;
                    ticking = false;
                });
                ticking = true;
            }
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [showNavBar, hideNavBar, resetNavBar]);
    
    // Mobile menu state
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isMobileView, setIsMobileView] = useState(screenWidth < 768);
    
    // Community modal state
    const [showCommunityModal, setShowCommunityModal] = useState(false);
    
    // Notifications state
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNotifications, setShowNotifications] = useState(false);
    
    // Watched fixtures state
    const [watchedFixtures, setWatchedFixtures] = useState([]);
    
    // Listen for window resize to update mobile view state
    useEffect(() => {
        if (Platform.OS !== 'web') return;
        
        const handleResize = () => {
            const width = Dimensions.get('window').width;
            setIsMobileView(width < 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Community settings are now loaded from NavigationContext
    // No need to fetch separately - NavigationContext's maintenance listener handles it

    // Listen for notifications
    useEffect(() => {
        if (!user?.uid) {
            setNotifications([]);
            setUnreadCount(0);
            return;
        }

        const fetchNotifications = async () => {
            try {
                const notificationsRef = collection(db, 'notifications');
                const q = query(
                    notificationsRef,
                    where('uid', '==', user.uid),
                    orderBy('createdAt', 'desc'),
                    firestoreLimit(20)
                );
                
                const snapshot = await getDocs(q);
                const notifs = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                
                setNotifications(notifs);
                setUnreadCount(notifs.filter(n => !n.read).length);
            } catch (e) {
                console.error('[NavigationHeader] Error fetching notifications:', e);
            }
        };

        fetchNotifications();

        // Poll for new notifications every 60 seconds
        const interval = setInterval(fetchNotifications, 60000);
        return () => clearInterval(interval);
    }, [user?.uid]);
    
    // Listen for watched fixtures
    useEffect(() => {
        if (!user?.uid) {
            setWatchedFixtures([]);
            return;
        }
        
        let unsubscribed = false;
        
        const watchersQuery = query(
            collection(db, 'query_watchers'),
            where('uid', '==', user.uid),
            where('status', '==', 'active'),
            orderBy('createdAt', 'desc'),
            firestoreLimit(10)
        );
        
        const unsub = onSnapshot(watchersQuery, (snapshot) => {
            if (unsubscribed) return;
            
            const fixtures = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            
            setWatchedFixtures(fixtures);
        }, (error) => {
            if (unsubscribed) return;
            console.error('[NavigationHeader] Error fetching watched fixtures:', error);
        });
        
        return () => {
            unsubscribed = true;
            unsub();
        };
    }, [user?.uid]);

    // Exit animation function for navigation header
    const exitAnimation = (callback) => {
        Animated.timing(navOpacity, {
            toValue: 0,
            duration: 400,
            easing: Easing.in(Easing.cubic),
            useNativeDriver: false,
        }).start(() => {
            if (callback) callback();
        });
    };

    const handleLoginPress = () => {
        exitAnimation(() => {
            navigateTo('Login');
        });
    };

    const navigate = (path) => navigateTo(path);

    // Toasts render app-wide in components/motion/Toaster (mounted in App.js).

    // Mobile menu row props/text: Floodlight glass rows, Classic unchanged.
    const mmItem = (active, classicStyle) => (fl
        ? { style: [flStyles.mobileMenuItem, active && flStyles.mobileMenuItemActive], hoverStyle: active ? null : flStyles.mobileMenuItemHover, scaleTo: 0.99, lift: 0, accessibilityRole: 'button', ...(active ? { accessibilityState: { selected: true } } : {}) }
        : { style: classicStyle || [styles.mobileMenuItem, active && styles.mobileMenuItemActive] });
    const mmText = (active) => (fl ? [flStyles.mobileMenuItemText, active && flStyles.mobileMenuItemTextActive] : styles.mobileMenuItemText);
    const mmDot = (active) => (fl && active ? <View style={flStyles.mobileMenuDot} /> : null);

    // Mobile Menu Component
    const MobileMenu = () => (
        <Modal
            visible={mobileMenuOpen}
            transparent
            animationType="slide"
            onRequestClose={() => setMobileMenuOpen(false)}
        >
            <Pressable 
                style={fl ? flStyles.mobileMenuOverlay : styles.mobileMenuOverlay}
                onPress={() => setMobileMenuOpen(false)}
            >
                <Pressable 
                    style={fl ? flStyles.mobileMenuContainer : styles.mobileMenuContainer}
                    onPress={(e) => e.stopPropagation()}
                >
                    {/* Menu Header */}
                    <View style={fl ? flStyles.mobileMenuHeader : styles.mobileMenuHeader}>
                        <Text style={fl ? flStyles.mobileMenuHeaderText : styles.mobileMenuHeaderText}>Menu</Text>
                        {fl ? (
                            <PressableScale onPress={() => setMobileMenuOpen(false)} style={flStyles.iconButton} hoverStyle={flStyles.iconButtonHover} accessibilityRole="button" accessibilityLabel="Close menu">
                                <Ionicons name="close" size={22} color="rgba(255,255,255,0.9)" />
                            </PressableScale>
                        ) : (
                        <PressableScale onPress={() => setMobileMenuOpen(false)}>
                            <Ionicons name="close" size={28} color="rgba(255,255,255,0.9)" />
                        </PressableScale>
                        )}
                    </View>

                    {/* Profile Section */}
                    {isLoggedIn && (
                        <PressableScale 
                            style={fl ? flStyles.mobileMenuProfile : styles.mobileMenuProfile}
                            onPress={() => {
                                setMobileMenuOpen(false);
                                navigateTo('Profile');
                            }}
                        >
                            {(userProfile?.profilePhoto || user?.profilePhoto || user?.photoURL) ? (
                                <Image 
                                    source={{ uri: userProfile?.profilePhoto || user?.profilePhoto || user?.photoURL }} 
                                    style={styles.mobileMenuProfileImage}
                                    resizeMode="cover" 
                                />
                            ) : (
                                <ProfileIcon size={48} />
                            )}
                            <View style={styles.mobileMenuProfileInfo}>
                                <Text style={fl ? flStyles.mobileMenuProfileName : styles.mobileMenuProfileName}>
                                    {userProfile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'User'}
                                </Text>
                                <Text style={fl ? flStyles.mobileMenuProfileEmail : styles.mobileMenuProfileEmail}>
                                    {user?.email || 'View Profile'}
                                </Text>
                            </View>
                        </PressableScale>
                    )}

                    {/* Menu Items */}
                    <ScrollView style={fl ? flStyles.mobileMenuItems : styles.mobileMenuItems}>
                        <PressableScale 
                            {...mmItem(currentScreen === 'Home')}
                            onPress={() => {
                                setMobileMenuOpen(false);
                                navigateTo('Home');
                            }}
                        >
                            <Text style={mmText(currentScreen === 'Home')}>HOME</Text>{mmDot(currentScreen === 'Home')}
                        </PressableScale>

                        <PressableScale 
                            {...mmItem(currentScreen === 'SameGameParlay')}
                            onPress={() => {
                                setMobileMenuOpen(false);
                                navigateTo('SameGameParlay');
                            }}
                        >
                            <Text style={mmText(currentScreen === 'SameGameParlay')}>OPTIMIZER</Text>{mmDot(currentScreen === 'SameGameParlay')}
                        </PressableScale>

                        <PressableScale 
                            {...mmItem(currentScreen === 'Community')}
                            onPress={() => {
                                setMobileMenuOpen(false);
                                if (communityScreenEnabled) {
                                    navigateTo('Community');
                                } else if (communityModalEnabled) {
                                    setShowCommunityModal(true);
                                } else {
                                    if (Platform.OS === 'web') {
                                        window.open(communityRedirectUrl, '_blank');
                                    }
                                }
                            }}
                        >
                            <Text style={mmText(currentScreen === 'Community')}>COMMUNITY</Text>{mmDot(currentScreen === 'Community')}
                        </PressableScale>

                        <PressableScale 
                            {...mmItem(currentScreen === 'Plans')}
                            onPress={() => {
                                setMobileMenuOpen(false);
                                navigateTo('Plans');
                            }}
                        >
                            <Text style={mmText(currentScreen === 'Plans')}>PLANS</Text>{mmDot(currentScreen === 'Plans')}
                        </PressableScale>

                        {effectiveIsAdmin && (
                            <PressableScale 
                                {...mmItem(currentScreen === 'Admin')}
                                onPress={() => {
                                    setMobileMenuOpen(false);
                                    navigateTo('Admin');
                                }}
                            >
                                <Text style={mmText(currentScreen === 'Admin')}>ADMIN</Text>{mmDot(currentScreen === 'Admin')}
                            </PressableScale>
                        )}

                        {!isLoggedIn && (
                            <PressableScale 
                                {...mmItem(currentScreen === 'Login')}
                                onPress={() => {
                                    setMobileMenuOpen(false);
                                    navigateTo('Login');
                                }}
                            >
                                <Text style={mmText(currentScreen === 'Login')}>LOGIN</Text>{mmDot(currentScreen === 'Login')}
                            </PressableScale>
                        )}

                        {isLoggedIn && (
                            <PressableScale 
                                {...mmItem(false, styles.mobileMenuItem)}
                                onPress={async () => {
                                    setMobileMenuOpen(false);
                                    await logout();
                                    navigateTo('Home');
                                }}
                            >
                                <Text style={[fl ? flStyles.mobileMenuItemText : styles.mobileMenuItemText, { color: 'rgba(255,100,100,0.9)' }]}>
                                    LOGOUT
                                </Text>
                            </PressableScale>
                        )}
                    </ScrollView>
                </Pressable>
            </Pressable>
        </Modal>
    );

    // Ticker shows if: enabled by default (settings) AND not collapsed (chevron) AND showTicker prop
    const shouldShowTicker = showTicker && isTickerEnabled;
    const tickerFullyVisible = shouldShowTicker && !isTickerCollapsed;
    const tickerCollapsedVisible = shouldShowTicker && isTickerCollapsed;
    const TOP_SPACER_HEIGHT = 16; // Space from top when ticker is disabled
    
    return (
        <View style={styles.outerWrapper}>
            {/* Game Ticker - full view */}
            {tickerFullyVisible && (
                <GameTicker 
                    fixtures={fixtures}
                    isVisible={tickerFullyVisible}
                    isCollapsed={isTickerCollapsed}
                    onCollapseToggle={toggleCollapsed}
                    isLoading={tickerLoading}
                    isLoadingMore={tickerLoadingMore}
                />
            )}
            
            {/* 🏥 Injury Ticker - shows below game ticker when enabled */}
            {tickerFullyVisible && isInjuryTickerEnabled && (
                <InjuryTicker isVisible={tickerFullyVisible} />
            )}
            
            {/* Collapsed ticker indicator - small expand bar */}
            {tickerCollapsedVisible && (
                <PressableScale 
                    onPress={toggleCollapsed}
                    style={styles.collapsedTicker}
                >
                    <Ionicons name="chevron-down" size={14} color="rgba(255,255,255,0.5)" />
                    <Text style={styles.collapsedTickerText}>Show Games</Text>
                    <Ionicons name="chevron-down" size={14} color="rgba(255,255,255,0.5)" />
                </PressableScale>
            )}
            
            {/* Spacer when ticker is disabled entirely */}
            {!shouldShowTicker && (
                <View style={{ height: TOP_SPACER_HEIGHT }} />
            )}
            
            {/* Navigation Bar - Animates in/out on scroll */}
        <Animated.View 
            style={[
                    styles.navAnimatedWrapper,
                { transform: [{ translateY: headerTranslateY }] }
            ]}
            onLayout={(e) => setHeaderHeight(e.nativeEvent.layout.height)}
        >
            <View style={styles.navContainer}>
            <Animated.View style={{ opacity: navOpacity }}>
                <View style={fl ? [styles.navBar, flStyles.navBar] : styles.navBar} {...(fl ? { dataSet: { flNavbar: '1' } } : {})}>
                    <View style={fl ? [styles.navLeft, flStyles.navLeft] : styles.navLeft}>
                        <PressableScale onPress={() => navigateTo('Home')} style={styles.iconWrap}>
                            <LogoIcon size={isTablet ? 26 : 30} />
                        </PressableScale>
                    </View>
                    
                    {/* Show full nav on desktop, hide on mobile */}
                    {!isMobileView && (
                        <View style={fl ? flStyles.navCenter : styles.navCenter}>
                            <NavItem fl={fl} active={currentScreen === 'Home'} label="HOME" dimmed={currentScreen !== 'Home'} onPress={() => navigateTo('Home')} />
                            <NavItem fl={fl} active={currentScreen === 'SameGameParlay'} label="OPTIMIZER" dimmed={currentScreen !== 'SameGameParlay'} onPress={() => navigate('SameGameParlay')} />
                            <NavItem 
                                fl={fl}
                                active={currentScreen === 'Community'}
                                label="COMMUNITY" 
                                dimmed={currentScreen !== 'Community'} 
                                onPress={() => {
                                    if (communityScreenEnabled) {
                                        navigateTo('Community');
                                    } else if (communityModalEnabled) {
                                        setShowCommunityModal(true);
                                    } else {
                                        // Redirect to external URL
                                        if (Platform.OS === 'web') {
                                            window.open(communityRedirectUrl, '_blank');
                                        }
                                    }
                                }} 
                            />
                            <NavItem fl={fl} active={currentScreen === 'Plans'} label="PLANS" dimmed={currentScreen !== 'Plans'} onPress={() => navigateTo('Plans')} />
                            {effectiveIsAdmin ? (
                              <NavItem fl={fl} active={currentScreen === 'Admin'} label="ADMIN" dimmed={currentScreen !== 'Admin'} onPress={() => navigateTo('Admin')} />
                            ) : null}
                        </View>
                    )}
                    
                    <View style={fl ? [styles.navRight, flStyles.navRight] : styles.navRight}>
                        {/* Notification Bell - Show for logged in users */}
                        {isLoggedIn && (
                            <PressableScale 
                                style={fl ? [flStyles.iconButton, showNotifications && flStyles.iconButtonOn] : { position: 'relative', padding: 8 }}
                                {...(fl ? { hoverStyle: showNotifications ? null : flStyles.iconButtonHover, accessibilityRole: 'button', accessibilityLabel: unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications' } : {})}
                                onPress={() => setShowNotifications(!showNotifications)}
                            >
                                <Ionicons name={showNotifications ? "notifications" : "notifications-outline"} size={fl ? 20 : (isTablet ? 22 : 24)} color={fl && showNotifications ? '#FFFFFF' : "rgba(255,255,255,0.9)"} />
                                {unreadCount > 0 && fl && (
                                    <View style={flStyles.badge}>
                                        <Text style={flStyles.badgeText}>{unreadCount > 9 ? '9+' : unreadCount}</Text>
                                    </View>
                                )}
                                {unreadCount > 0 && !fl && (
                                    <View style={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        backgroundColor: '#FF4444',
                                        borderRadius: 10,
                                        minWidth: 18,
                                        height: 18,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        paddingHorizontal: 4,
                                    }}>
                                        <Text style={{ color: '#FFF', fontSize: 10, fontWeight: '700' }}>
                                            {unreadCount > 9 ? '9+' : unreadCount}
                                        </Text>
                                    </View>
                                )}
                            </PressableScale>
                        )}
                        
                        {/* Show hamburger menu on mobile, profile/login on desktop */}
                        {isMobileView ? (
                            <PressableScale 
                                style={fl ? flStyles.iconButton : styles.hamburgerButton}
                                {...(fl ? { hoverStyle: flStyles.iconButtonHover, accessibilityRole: 'button', accessibilityLabel: 'Open menu' } : {})}
                                onPress={() => setMobileMenuOpen(true)}
                            >
                                <Ionicons name="menu" size={fl ? 22 : 28} color="rgba(255,255,255,0.9)" />
                            </PressableScale>
                        ) : (
                            <>
                                {isLoggedIn ? (
                                    <View style={styles.iconWrap}>
                                        <PressableScale onPress={() => navigateTo('Profile')} {...(fl ? { style: [flStyles.avatar, currentScreen === 'Profile' && flStyles.avatarActive], hoverStyle: flStyles.avatarHover, accessibilityRole: 'button', accessibilityLabel: 'Profile' } : {})}>
                                            {(userProfile?.profilePhoto || user?.profilePhoto || user?.photoURL) ? (
                                              <Image source={{ uri: userProfile?.profilePhoto || user?.profilePhoto || user?.photoURL }} style={{ width: isTablet ? 32 : 36, height: isTablet ? 32 : 36, borderRadius: isTablet ? 16 : 18 }} resizeMode="cover" />
                                            ) : (
                                              <ProfileIcon size={isTablet ? 32 : 36} />
                                            )}
                                        </PressableScale>
                                    </View>
                                ) : (
                                    <NavItem fl={fl} cta label="LOGIN" onPress={handleLoginPress} />
                                )}
                            </>
                        )}
                    </View>
                </View>
            </Animated.View>
            
            {/* Notifications Dropdown */}
            {showNotifications && isLoggedIn && (
                <View style={[{
                    position: 'absolute',
                    top: 70,
                    right: isMobileView ? 60 : 120,
                    width: isMobileView ? screenWidth - 40 : 400,
                    maxHeight: 500,
                    backgroundColor: '#1a1a1a',
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: 'rgba(255,255,255,0.1)',
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.3,
                    shadowRadius: 12,
                    zIndex: 9999,
                    elevation: 9999,
                    }, fl && flStyles.notifPanel]}>
                    {/* Header */}
                    <View style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 16,
                        borderBottomWidth: 1,
                        borderBottomColor: 'rgba(255,255,255,0.1)',
                    }}>
                        <Text style={fl ? flStyles.notifTitle : { fontSize: 16, fontWeight: '700', color: '#FFF' }}>
                            Notifications
                        </Text>
                        <PressableScale onPress={() => setShowNotifications(false)}>
                            <Ionicons name="close" size={24} color="rgba(255,255,255,0.7)" />
                        </PressableScale>
                    </View>
                    
                    {/* Notifications List */}
                    <ScrollView style={{ maxHeight: 400 }}>
                        {notifications.length === 0 ? (
                            <View style={{ padding: 32, alignItems: 'center' }}>
                                <Ionicons name="notifications-off-outline" size={48} color="rgba(255,255,255,0.3)" />
                                <Text style={{ color: 'rgba(255,255,255,0.5)', marginTop: 12, fontSize: 14 }}>
                                    No notifications yet
                                </Text>
                            </View>
                        ) : (
                            notifications.map((notif) => (
                                <PressableScale
                                    key={notif.id}
                                    style={{
                                        padding: 16,
                                        borderBottomWidth: 1,
                                        borderBottomColor: 'rgba(255,255,255,0.05)',
                                        backgroundColor: notif.read ? 'transparent' : 'rgba(162, 89, 255, 0.05)',
                                    }}
                                    onPress={async () => {
                                        // Mark as read
                                        if (!notif.read) {
                                            try {
                                                await updateDoc(doc(db, 'notifications', notif.id), { read: true });
                                                setNotifications(prev => prev.map(n => 
                                                    n.id === notif.id ? { ...n, read: true } : n
                                                ));
                                                setUnreadCount(prev => Math.max(0, prev - 1));
                                            } catch (e) {
                                                console.error('Error marking notification as read:', e);
                                            }
                                        }
                                        
                                        // Navigate to SgpFlow with the fixture details
                                        if (notif.type === 'odds_available' && notif.fixtureTitle) {
                                            setShowNotifications(false);
                                            navigateTo('SgpFlow', { payload: { query: notif.fixtureTitle } });
                                        }
                                    }}
                                >
                                    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
                                        <View>
                                            <Text style={{ fontSize: 14, fontWeight: '600', color: '#FFF', marginBottom: 4 }}>
                                                {notif.title}
                                            </Text>
                                            <Text style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>
                                                {notif.message}
                                            </Text>
                                            {notif.createdAt && (
                                                <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                                                    {notif.createdAt.toDate ? new Date(notif.createdAt.toDate()).toLocaleString() : 'Recently'}
                                                </Text>
                                            )}
                                        </View>
                                        {!notif.read && (
                                            <View style={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: 4,
                                                backgroundColor: '#A259FF',
                                                marginTop: 4,
                                            }} />
                                        )}
                                    </View>
                                </PressableScale>
                            ))
                        )}
                        
                        {/* Watched Fixtures Section */}
                        {watchedFixtures.length > 0 && (
                            <>
                                {/* Divider */}
                                <View style={{
                                    height: 1,
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    marginVertical: 12,
                                    marginHorizontal: 16,
                                }} />
                                
                                {/* Section Header */}
                                <View style={{ paddingHorizontal: 16, marginBottom: 8 }}>
                                    <Text style={{ fontSize: 12, fontWeight: '700', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>
                                        Watching ({watchedFixtures.length})
                                    </Text>
                                </View>
                                
                                {/* Watched Fixtures List */}
                                {watchedFixtures.map((fixture) => (
                                    <PressableScale
                                        key={fixture.id}
                                        style={{
                                            padding: 16,
                                            borderBottomWidth: 1,
                                            borderBottomColor: 'rgba(255,255,255,0.05)',
                                            backgroundColor: 'transparent',
                                        }}
                                        onPress={() => {
                                            setShowNotifications(false);
                                            navigateTo('SgpFlow', { 
                                                payload: { 
                                                    query: `${fixture.homeTeam} vs ${fixture.awayTeam}` 
                                                } 
                                            });
                                        }}
                                    >
                                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <View style={{ flex: 1 }}>
                                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                    <Ionicons name="eye-outline" size={14} color="#A259FF" />
                                                    <Text style={{ fontSize: 11, color: '#A259FF', fontWeight: '600', textTransform: 'uppercase' }}>
                                                        {fixture.sport || 'Sports'}
                                                    </Text>
                                                </View>
                                                <Text style={{ fontSize: 14, fontWeight: '600', color: '#FFF', marginBottom: 2 }}>
                                                    {fixture.homeTeam} vs {fixture.awayTeam}
                                                </Text>
                                                <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
                                                    {fixture.league || 'League'}
                                                </Text>
                                                {fixture.commenceTime && (
                                                    <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                                                        {fixture.commenceTime.toDate ? new Date(fixture.commenceTime.toDate()).toLocaleString() : 'TBD'}
                                                    </Text>
                                                )}
                                            </View>
                                            <Ionicons name="chevron-forward" size={16} color="rgba(255,255,255,0.3)" />
                                        </View>
                                    </PressableScale>
                                ))}
                            </>
                        )}
                    </ScrollView>
                </View>
            )}
            
            <MobileMenu />
            
            <CommunityModal
                visible={showCommunityModal}
                onClose={() => setShowCommunityModal(false)}
            />
        </View>
        </Animated.View>
        </View>
    );
}

export default React.memo(NavigationHeader);